import { useEffect, useRef } from "react";

/**
 * Slim fixed reading-progress bar that fills as the user scrolls.
 *
 * Two things here exist to avoid forced reflows, which Lighthouse attributed
 * ~130 ms of layout time to in the previous version:
 *
 *  - The bar is driven by `transform: scaleX()` written straight to the node,
 *    not by React state and not by `width`. Animating `width` invalidates
 *    layout on every frame; a transform is composited and touches neither
 *    layout nor React's render path.
 *  - `scrollHeight` is read once and cached. Reading it every frame, right
 *    after the previous frame dirtied the layout, forced a synchronous
 *    re-layout on each scroll event. It only changes on resize or when the
 *    document itself grows, so a ResizeObserver keeps it current.
 */
const ReadingProgress = () => {
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let rafId: number | null = null;
    let maxScroll = 0;

    const measure = () => {
      maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    };

    const paint = () => {
      rafId = null;
      const pct = maxScroll > 0 ? Math.min(1, Math.max(0, window.scrollY / maxScroll)) : 0;
      if (barRef.current) barRef.current.style.transform = `scaleX(${pct})`;
    };

    const onScroll = () => {
      if (rafId === null) rafId = window.requestAnimationFrame(paint);
    };

    const onResize = () => {
      measure();
      onScroll();
    };

    measure();
    paint();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    // Lazy-loaded images and route changes alter the document height without
    // a resize event, so watch the element itself.
    const observer =
      "ResizeObserver" in window ? new ResizeObserver(onResize) : null;
    observer?.observe(document.documentElement);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      observer?.disconnect();
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, []);

  return <div ref={barRef} className="reading-progress" aria-hidden="true" />;
};

export default ReadingProgress;
